import { FC, ReactNode, createContext, useCallback, useContext, useState } from "react";

import { AlertProps, ToastContainer } from "@theme/components";

interface AddToastProps {
  title: AlertProps["title"];
  message?: AlertProps["message"];
  variant?: AlertProps["variant"];
  theme?: AlertProps["theme"];
  autoClose?: AlertProps["autoClose"];
}

interface ToastContextProps {
  addToast: (props: AddToastProps) => void;
  removeToast: (id: number) => void;
}

export const ToastContext = createContext({} as ToastContextProps);

let id = 1;

interface ToastProviderProps {
  children: ReactNode;
}

export const ToastProvider: FC<ToastProviderProps> = ({ children }) => {
  const [toasts, setToasts] = useState([] as AlertProps[]);

  const addToast: ToastContextProps["addToast"] = useCallback(
    ({ title, message, variant = "success", theme = "LIGHT", autoClose }) => {
      setToasts((toasts) => [
        ...toasts,
        {
          id: id++,
          title,
          message,
          variant,
          theme,
          autoClose,
        },
      ]);
    },
    [setToasts],
  );

  const removeToast = useCallback(
    (id: number) => {
      setToasts((toasts) => toasts.filter((toast) => toast.id !== id));
    },
    [setToasts],
  );

  return (
    <ToastContext.Provider value={{ addToast, removeToast }}>
      {toasts.length > 0 && <ToastContainer toasts={toasts} />}
      {children}
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  return useContext(ToastContext);
};
